import { useContext } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { DiscoveryContext } from "../../luminaire/DiscoveryV2/discovery-context";
import { getPurchaseCriteriaDetail, getPurchaseCriteriaAvg } from "../../utils/brandIndexCalc";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

function scoreColor(score) {
  if (score == null) return "#8a94a6";
  if (score >= 9) return "#198754";
  if (score >= 7) return "#2196F3";
  return "#eca336";
}

/**
 * Shows all purchase criteria (category dimension) ratings for a single brand/competitor.
 * Reads directly from DiscoveryContext.
 */
export default function PurchaseCriteriaTable({ brandProduct, categoryDimensionsData }) {
  const discoveryCtx = useContext(DiscoveryContext);
  const source = categoryDimensionsData ?? discoveryCtx.category_dimensions;
  const rows = getPurchaseCriteriaDetail(brandProduct, source);
  const avg = getPurchaseCriteriaAvg(brandProduct, source);

  if (!rows.length) {
    return (
      <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
        Purchase Criteria data not yet available.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table sx={{ width: "100%", tableLayout: "fixed" }} size="medium">
        <colgroup>
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "60%" }} />
        </colgroup>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Dimension</StyledTableCell>
            <StyledTableCell align="center">Rating (0–10)</StyledTableCell>
            <StyledTableCell>Explanation</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow key={row.name}>
              <StyledTableCell component="th" scope="row">{row.name}</StyledTableCell>
              <StyledTableCell align="center">
                <span style={{ color: scoreColor(row.rating), fontWeight: 600 }}>
                  {row.rating != null ? Number(row.rating).toFixed(1) : "—"}
                </span>
              </StyledTableCell>
              <StyledTableCell sx={{ wordBreak: "break-word" }}>
                <Typography variant="body2" color="textSecondary" sx={{ fontSize: 12 }}>
                  {row.explanation || "—"}
                </Typography>
              </StyledTableCell>
            </StyledTableRow>
          ))}
          <TableRow sx={{ backgroundColor: "#f0f4f8" }}>
            <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>Average</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, color: scoreColor(avg) }}>
              {avg != null ? avg.toFixed(2) : "—"}
            </TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
